
import React, { useState } from 'react';
import { Lightbulb, Brain, Target, Code, DollarSign, Image, Search, List, FileText, Factory, Globe, Smartphone } from 'lucide-react';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type Platform = 'web' | 'mobile' | 'desktop' | 'all';
type Complexity = 'simple' | 'medium' | 'complex';

interface IdeaFactoryState {
  ideaDescription: string;
  targetAudience: string;
  platform: Platform;
  complexity: Complexity;
  keyFeatures: string;
  budget: string;
  timeline: string;
  isSubmitting: boolean;
  step: number;
  result: IdeaResult | null;
}

interface IdeaResult {
  structuredIdea: {
    title: string;
    summary: string;
    targetPlatform: Platform;
    estimatedComplexity: Complexity;
    suggestedFeatures: string[];
    technicalConsiderations: string[];
  };
  priceEstimate: {
    min: number;
    max: number;
    currency: string;
    timeframe: string;
  };
  visualPreview: string;
}

const mockProcessIdea = (data: Partial<IdeaFactoryState>): Promise<IdeaResult> => {
  // This would be replaced with an actual API call in production
  return new Promise((resolve) => {
    setTimeout(() => {
      // Generate a mock result based on the input
      const complexity = data.complexity || 'medium';
      const platform = data.platform || 'web';
      
      const complexityFactors = {
        simple: { min: 5000, max: 15000, features: 3 },
        medium: { min: 15000, max: 35000, features: 5 },
        complex: { min: 35000, max: 100000, features: 8 },
      };
      
      const platformDescriptions = {
        web: "application web responsive",
        mobile: "application mobile native",
        desktop: "application desktop multi-plateforme",
        all: "solution multi-plateforme (web, mobile et desktop)"
      };
      
      const factor = complexityFactors[complexity];
      const description = data.ideaDescription || "une application innovante";
      
      // Generate features based on the input
      const featureIdeas = [
        "Authentification sécurisée",
        "Interface utilisateur intuitive",
        "Système de notifications",
        "Profils utilisateurs personnalisables",
        "Tableau de bord analytique",
        "Intégration de paiements",
        "Fonctionnalités de recherche avancée",
        "Messagerie instantanée",
        "Système de recommandations"
      ];
      
      const suggestedFeatures = featureIdeas.slice(0, factor.features);
      
      // Technical considerations based on platform
      const techConsiderations = {
        web: [
          "Architecture frontend React.js avec Typescript",
          "Backend Node.js avec Express ou Next.js",
          "Base de données PostgreSQL ou MongoDB",
          "Hébergement cloud sur AWS ou Vercel"
        ],
        mobile: [
          "Développement natif (Swift pour iOS, Kotlin pour Android)",
          "Ou solution cross-platform (React Native, Flutter)",
          "Backend API RESTful ou GraphQL",
          "Stratégie de déploiement sur App Store et Play Store"
        ],
        desktop: [
          "Développement avec Electron pour multi-plateforme",
          "Architecture modulaire pour maintenance facilitée",
          "Système de mise à jour automatique",
          "Gestion du stockage local et synchronisation cloud"
        ],
        all: [
          "Architecture centralisée avec API commune",
          "Conception UI/UX adaptative",
          "Stratégie de code partagé pour réduire la duplication",
          "Système de synchronisation de données entre plateformes"
        ]
      };
      
      resolve({
        structuredIdea: {
          title: `${description.charAt(0).toUpperCase() + description.slice(1)}`,
          summary: `Une ${platformDescriptions[platform]} qui permettra ${description.toLowerCase()}`,
          targetPlatform: platform,
          estimatedComplexity: complexity,
          suggestedFeatures: suggestedFeatures,
          technicalConsiderations: techConsiderations[platform],
        },
        priceEstimate: {
          min: factor.min,
          max: factor.max,
          currency: "EUR",
          timeframe: complexity === 'simple' ? "1-2 mois" : complexity === 'medium' ? "2-4 mois" : "4-8 mois"
        },
        visualPreview: "https://placehold.co/600x400/e2e8f0/1e293b?text=Aperçu+Conceptuel"
      });
    }, 2000);
  });
};

const IdeaFactoryForm = () => {
  const [state, setState] = useState<IdeaFactoryState>({
    ideaDescription: '',
    targetAudience: '',
    platform: 'web',
    complexity: 'medium',
    keyFeatures: '',
    budget: '',
    timeline: '',
    isSubmitting: false,
    step: 1,
    result: null
  });

  const updateState = (field: keyof IdeaFactoryState, value: any) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (state.step === 1 && !state.ideaDescription) {
      toast.error("Merci de décrire votre idée avant de continuer");
      return;
    }
    
    setState(prev => ({ ...prev, step: prev.step + 1 }));
  };

  const prevStep = () => {
    setState(prev => ({ ...prev, step: Math.max(1, prev.step - 1) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setState(prev => ({ ...prev, isSubmitting: true }));
    
    try {
      // In a real app, this would call an API
      const result = await mockProcessIdea(state);
      setState(prev => ({ 
        ...prev, 
        isSubmitting: false,
        step: 4,
        result 
      }));
    } catch (error) {
      toast.error("Une erreur est survenue lors du traitement de votre idée.");
      setState(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center max-w-3xl mx-auto">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex flex-col items-center">
              <div 
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium mb-2",
                  state.step === step 
                    ? "bg-orcsys-500 text-white" 
                    : state.step > step 
                      ? "bg-orcsys-100 text-orcsys-500" 
                      : "bg-gray-100 text-gray-400"
                )}
              >
                {step === 1 && <Lightbulb size={18} />}
                {step === 2 && <Target size={18} />}
                {step === 3 && <Code size={18} />}
                {step === 4 && <Brain size={18} />}
              </div>
              <span 
                className={cn(
                  "text-xs",
                  state.step === step 
                    ? "text-orcsys-500 font-medium" 
                    : "text-gray-500"
                )}
              >
                {step === 1 && "Idée"}
                {step === 2 && "Cible"}
                {step === 3 && "Détails"}
                {step === 4 && "Résultat"}
              </span>
            </div>
          ))}
        </div>
        <div className="h-1 bg-gray-100 max-w-3xl mx-auto mt-2 rounded">
          <div 
            className="h-1 bg-orcsys-500 rounded-l transition-all duration-500 ease-in-out" 
            style={{ width: `${(state.step - 1) * 33.33}%` }}
          />
        </div>
      </div>
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-orcsys-50 via-white to-orcsys-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-orcsys-100 text-orcsys-600 rounded-full mb-4">
            <Factory className="w-6 h-6" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Idea Factory</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Décrivez votre idée d'application et laissez-nous la structurer, vous donner des recommandations techniques et une estimation de prix.
          </p>
        </div>

        {renderStepIndicator()}

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {state.step === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Lightbulb className="w-6 h-6 text-orcsys-500" />
                  Parlez-nous de votre idée
                </h2>
                <div>
                  <label htmlFor="ideaDescription" className="block text-sm font-medium text-slate-700 mb-2">
                    Décrivez votre idée d'application ou de service numérique *
                  </label>
                  <textarea
                    id="ideaDescription"
                    rows={6}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all"
                    placeholder="Ex: Je souhaite créer une application qui met en relation des propriétaires de jardins avec des jardiniers amateurs qui cherchent un espace pour cultiver des légumes..."
                    value={state.ideaDescription}
                    onChange={(e) => updateState('ideaDescription', e.target.value)}
                    required
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={nextStep} className="px-6">
                    Suivant
                  </Button>
                </div>
              </div>
            )}

            {state.step === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Target className="w-6 h-6 text-orcsys-500" />
                  Cible et Plateforme
                </h2>
                <div>
                  <label htmlFor="targetAudience" className="block text-sm font-medium text-slate-700 mb-2">
                    Qui sont les utilisateurs cibles de votre application ? *
                  </label>
                  <textarea
                    id="targetAudience"
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all"
                    placeholder="Ex: Des particuliers âgés de 25 à 45 ans intéressés par le jardinage mais qui n'ont pas d'espace..."
                    value={state.targetAudience}
                    onChange={(e) => updateState('targetAudience', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Sur quelle(s) plateforme(s) souhaitez-vous déployer votre solution ? *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                    {[
                      { id: 'web', label: 'Web', icon: <Globe className="w-5 h-5" /> },
                      { id: 'mobile', label: 'Mobile', icon: <Smartphone className="w-5 h-5" /> },
                      { id: 'desktop', label: 'Desktop', icon: <Code className="w-5 h-5" /> },
                      { id: 'all', label: 'Toutes plateformes', icon: <Target className="w-5 h-5" /> }
                    ].map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={cn(
                          "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all hover:border-orcsys-300",
                          state.platform === item.id 
                            ? "border-orcsys-500 bg-orcsys-50" 
                            : "border-slate-200"
                        )}
                        onClick={() => updateState('platform', item.id)}
                      >
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center mb-2",
                          state.platform === item.id 
                            ? "bg-orcsys-100 text-orcsys-600" 
                            : "bg-slate-100 text-slate-600"
                        )}>
                          {item.icon}
                        </div>
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={prevStep}>
                    Précédent
                  </Button>
                  <Button onClick={nextStep}>
                    Suivant
                  </Button>
                </div>
              </div>
            )}

            {state.step === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Code className="w-6 h-6 text-orcsys-500" />
                  Détails et fonctionnalités
                </h2>
                <div>
                  <label htmlFor="keyFeatures" className="block text-sm font-medium text-slate-700 mb-2">
                    Quelles sont les fonctionnalités clés que vous envisagez ?
                  </label>
                  <textarea
                    id="keyFeatures"
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all"
                    placeholder="Ex: Système de chat entre utilisateurs, système de paiement, localisation sur carte..."
                    value={state.keyFeatures}
                    onChange={(e) => updateState('keyFeatures', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Quelle est la complexité approximative de votre projet ?
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                    {[
                      { id: 'simple', label: 'Simple', description: 'Fonctionnalités basiques, interface simple' },
                      { id: 'medium', label: 'Moyenne', description: 'Plusieurs fonctionnalités, quelques intégrations' },
                      { id: 'complex', label: 'Complexe', description: 'Nombreuses fonctionnalités, intégrations multiples' }
                    ].map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={cn(
                          "flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left hover:border-orcsys-300",
                          state.complexity === item.id 
                            ? "border-orcsys-500 bg-orcsys-50" 
                            : "border-slate-200"
                        )}
                        onClick={() => updateState('complexity', item.id)}
                      >
                        <span className="font-medium mb-1">{item.label}</span>
                        <span className="text-sm text-slate-500">{item.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-slate-700 mb-2">
                      Budget approximatif (si connu)
                    </label>
                    <input
                      type="text"
                      id="budget"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all"
                      placeholder="Ex: 15 000 € - 25 000 €"
                      value={state.budget}
                      onChange={(e) => updateState('budget', e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-slate-700 mb-2">
                      Délai souhaité pour le développement
                    </label>
                    <input
                      type="text"
                      id="timeline"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all"
                      placeholder="Ex: 3 mois"
                      value={state.timeline}
                      onChange={(e) => updateState('timeline', e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={prevStep} type="button">
                    Précédent
                  </Button>
                  <Button type="submit" disabled={state.isSubmitting}>
                    {state.isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyse en cours...
                      </>
                    ) : (
                      "Générer l'analyse"
                    )}
                  </Button>
                </div>
              </form>
            )}

            {state.step === 4 && state.result && (
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Brain className="w-6 h-6 text-orcsys-500" />
                  Analyse de votre idée
                </h2>

                <div className="bg-orcsys-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orcsys-500" />
                    Idée structurée
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-slate-700">Titre du projet</h4>
                      <p className="text-lg font-medium text-orcsys-700">{state.result.structuredIdea.title}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-700">Résumé</h4>
                      <p className="text-slate-800">{state.result.structuredIdea.summary}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-slate-700">Plateforme cible</h4>
                        <p className="text-slate-800 capitalize">{state.result.structuredIdea.targetPlatform}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-700">Complexité estimée</h4>
                        <p className="text-slate-800 capitalize">{state.result.structuredIdea.estimatedComplexity}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <List className="w-5 h-5 text-orcsys-500" />
                      Fonctionnalités suggérées
                    </h3>
                    <ul className="space-y-2">
                      {state.result.structuredIdea.suggestedFeatures.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-orcsys-100 text-orcsys-500 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                            <span className="text-xs font-medium">{index + 1}</span>
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <Code className="w-5 h-5 text-orcsys-500" />
                      Considérations techniques
                    </h3>
                    <ul className="space-y-2">
                      {state.result.structuredIdea.technicalConsiderations.map((tech, index) => (
                        <li key={index} className="flex items-start">
                          <div className="w-2 h-2 rounded-full bg-orcsys-400 mr-2 flex-shrink-0 mt-2"></div>
                          <span>{tech}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-slate-200">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-orcsys-500" />
                    Estimation de prix
                  </h3>
                  <div className="flex items-center justify-center">
                    <div className="text-center px-8 py-6 bg-slate-50 rounded-lg">
                      <p className="text-slate-500 mb-2">Budget estimé</p>
                      <div className="text-3xl font-bold text-slate-900">
                        {formatCurrency(state.result.priceEstimate.min)} - {formatCurrency(state.result.priceEstimate.max)}
                      </div>
                      <p className="text-slate-500 mt-2">Délai approximatif: {state.result.priceEstimate.timeframe}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-4 text-center">
                    Cette estimation est basée sur les informations fournies et peut varier selon les spécifications détaillées du projet.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Image className="w-5 h-5 text-orcsys-500" />
                    Aperçu visuel conceptuel
                  </h3>
                  <div className="bg-white p-4 rounded-lg border border-slate-200 flex justify-center">
                    <img 
                      src={state.result.visualPreview} 
                      alt="Aperçu conceptuel" 
                      className="rounded-lg max-w-full h-auto"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-2 text-center">
                    Cet aperçu est généré automatiquement et représente un concept visuel de votre idée.
                  </p>
                </div>

                <div className="pt-4 flex flex-col md:flex-row justify-center gap-4">
                  <Button variant="outline" onClick={() => setState({
                    ideaDescription: '',
                    targetAudience: '',
                    platform: 'web',
                    complexity: 'medium',
                    keyFeatures: '',
                    budget: '',
                    timeline: '',
                    isSubmitting: false,
                    step: 1,
                    result: null
                  })}>
                    Nouvelle analyse
                  </Button>
                  <Button.Link href="/contact">
                    Discuter de ce projet avec nous
                  </Button.Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdeaFactoryForm;
